import type {
  AysInekInlierType,
} from '@bindoc-projects/code-domain-lib';
import {
  AYS_INEK_INLIER_TYPES_DISPLAY_NAMES,
  compareInlierTypes,
} from '@bindoc-projects/code-domain-lib';
import { BdArrayUtil } from '@bindoc-projects/util-domain-lib';
import type {
  FilterConditionPluginSpecification,
} from '../filter.plugin';
import {
  FilterConditionPluginImpl
} from '../filter.plugin';
import type {
  BdCondition,
} from '@bindoc-projects/condition-domain-lib';
import {
  BdContentCondition,
} from '@bindoc-projects/condition-domain-lib';
import type { FilterDetailedText } from '../filter-detailed-text';

export const BD_INLIER_TYPE_CONDITION_ID = 'inlierType';
export const FilterConditionInlierTypesPluginType: FilterConditionPluginSpecification<
  AysInekInlierType[],
  FilterConditionInlierTypesDto,
  BdInlierTypesCondition
> = {
  type: 'inlierTypes',
  category: 'case',
  displayName: { de: 'Verweildauer' },
  sortingIndex: 8,
  occurrence: 'single',
  fromObject(
    obj: FilterConditionInlierTypesDto,
  ): FilterConditionPluginImpl<AysInekInlierType[]> {
    return new FilterConditionInlierTypesPlugin(
        obj.inlierTypes as AysInekInlierType[],
      );
  },
  fromCondition(
    jsonObj: BdInlierTypesCondition,
  ): FilterConditionPluginImpl<AysInekInlierType[]> {
    return new FilterConditionInlierTypesPlugin(jsonObj.includes ?? [])
  },
  newInstance(): FilterConditionPluginImpl<AysInekInlierType[]> {
    return new FilterConditionInlierTypesPlugin([]);
  },
};

export class FilterConditionInlierTypesPlugin extends FilterConditionPluginImpl<
  AysInekInlierType[],
  FilterConditionInlierTypesDto
> {
  public static override Specification: FilterConditionPluginSpecification<
    AysInekInlierType[],
    FilterConditionInlierTypesDto,
    BdInlierTypesCondition
  > = FilterConditionInlierTypesPluginType;

  constructor(private inlierTypes: AysInekInlierType[]) {
    super(FilterConditionInlierTypesPluginType.type);
  }

  clone(): FilterConditionInlierTypesPlugin {
    return new FilterConditionInlierTypesPlugin([...this.inlierTypes]);
  }

  override equals(other: FilterConditionInlierTypesPlugin): boolean {
    return (
      super.equals(other) &&
      BdArrayUtil.equals(this.inlierTypes, other.inlierTypes)
    );
  }

  override validationToText(): string {
    return this.valid() ? '' : 'Mindestens eine Verweildauer auswählen';
  }

  toCondition(): BdCondition | null {
    return new BdInlierTypesCondition(this.inlierTypes);
  }

  toJSON(): FilterConditionInlierTypesDto | null {
    return {
      type: this.type,
      inlierTypes: this.inlierTypes,
    };
  }

  toText(): string | null {
    return this.inlierTypes.length === 1
      ? 'ein Typ der Verweildauer'
      : `${this.inlierTypes.length} Typen der Verweildauer`;
  }

  override toDetailedText(): FilterDetailedText | null {
    const title = this.toText();
    if (!title) return null;
    const items = [...this.inlierTypes]
      .sort(compareInlierTypes)
      .map((type) => AYS_INEK_INLIER_TYPES_DISPLAY_NAMES[type] ?? type);
    return { title, items };
  }

  toValue(): AysInekInlierType[] {
    return this.inlierTypes;
  }
}

export class BdInlierTypesCondition extends BdContentCondition<AysInekInlierType> {
  constructor(includes: AysInekInlierType[]) {
    super(BD_INLIER_TYPE_CONDITION_ID, includes);
  }
}

interface FilterConditionInlierTypesDto {
  type: string;
  inlierTypes: string[];
}
